/** Estados canónicos reutilizables: loading (skeleton), vacío (ilustración + acción), error (reintento). */
import type { SucursalDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, Inbox, RefreshCw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ErrorApi, get, mensajeDeError } from '@/lib/api.ts';
import { useAuthOpcional } from '@/lib/auth.tsx';
import { claves } from '@/lib/query.ts';
import { DialogoOperarSucursal } from './DialogoOperarSucursal.tsx';
import { cn } from '@/lib/utils.ts';
import { Button } from './ui/button.tsx';
import { Skeleton } from './ui/skeleton.tsx';

export function Cargando({ filas = 5, className }: { filas?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} role="status" aria-live="polite" aria-label="Cargando">
      {Array.from({ length: filas }, (_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

export function Vacio({ titulo = 'Nada por aquí', descripcion, accion, icono, className }: { titulo?: string; descripcion?: string; accion?: ReactNode; icono?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed bg-card px-6 py-12 text-center', className)}>
      <div className="rounded-full bg-muted p-3 text-muted-foreground">{icono ?? <Inbox className="h-6 w-6" />}</div>
      <p className="font-medium">{titulo}</p>
      {descripcion ? <p className="max-w-sm text-sm text-muted-foreground">{descripcion}</p> : null}
      {accion ? <div className="mt-2">{accion}</div> : null}
    </div>
  );
}

/**
 * Rol global sin sucursal (ADR-0003) entrando a un módulo que opera una sucursal:
 * el superadmin puede tomar contexto desde el consolidado; el contador no toma contexto y consulta por Reportes/Libros.
 */
/** Superadmin en consola global: elige la sucursal aquí mismo y sigue en la pantalla donde estaba. */
function ElegirSucursalAqui() {
  const sucursales = useQuery({ queryKey: claves.sucursales, queryFn: () => get<SucursalDTO[]>('/sucursales'), staleTime: 5 * 60_000 });
  const [objetivo, setObjetivo] = useState<{ id: string; nombre: string } | null>(null);
  const activas = (sucursales.data ?? []).filter((s) => s.activa);
  return (
    <>
      <div className="mt-2 flex flex-wrap justify-center gap-2">
        {activas.map((s) => (
          <Button key={s.id} size="sm" onClick={() => setObjetivo({ id: s.id, nombre: s.nombre })} data-testid={`operar-aqui-${s.codigo}`}>
            Operar {s.nombre}
          </Button>
        ))}
        {sucursales.isLoading ? <span className="text-xs text-muted-foreground">Cargando sucursales…</span> : null}
      </div>
      <DialogoOperarSucursal sucursal={objetivo} cerrar={() => setObjetivo(null)} alTomar={() => undefined} motivoInicial={`Operación desde ${window.location.pathname}`} />
    </>
  );
}

export function RequiereSucursal({ className }: { className?: string }) {
  const usuario = useAuthOpcional()?.usuario ?? null;
  // Sin proveedor (tests) se asume la guía del superadmin
  const puedeTomarContexto = usuario === null || usuario.rol === 'superadmin';
  return (
    <div role="status" data-testid="requiere-sucursal" className={cn('flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed bg-card px-6 py-10 text-center', className)}>
      <div className="rounded-full bg-muted p-3 text-muted-foreground">
        <Building2 className="h-6 w-6" />
      </div>
      <p className="font-medium">{puedeTomarContexto ? 'Estás en la consola global' : 'Este módulo opera sobre una sucursal'}</p>
      <p className="max-w-md text-sm text-muted-foreground">
        {puedeTomarContexto
          ? 'Este módulo opera sobre una sucursal. Elige cuál vas a operar y sigues aquí mismo (también desde el selector de la cabecera).'
          : 'Tu rol es global y no opera cajas ni ventas de una sucursal. Lo consolidado de todas las sucursales está en Reportes y Libros.'}
      </p>
      {usuario?.rol === 'superadmin' ? (
        <ElegirSucursalAqui />
      ) : (
        <Button asChild size="sm" className="mt-2">
          {puedeTomarContexto ? <Link to="/consolidado">Elegir sucursal</Link> : <Link to="/reportes">Ir a Reportes</Link>}
        </Button>
      )}
    </div>
  );
}

export function ErrorEstado({ error, reintentar, className }: { error: unknown; reintentar?: () => void; className?: string }) {
  if (error instanceof ErrorApi && error.codigo === 'sin_sucursal') return <RequiereSucursal {...(className ? { className } : {})} />;
  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-6 py-10 text-center', className)}>
      <div className="rounded-full bg-red-100 p-3 text-mg-rojo">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <p className="font-medium text-red-900">No se pudo cargar</p>
      <p className="max-w-md text-sm text-red-800">{mensajeDeError(error)}</p>
      {reintentar ? (
        <Button variant="outline" size="sm" onClick={reintentar} className="mt-2">
          <RefreshCw /> Reintentar
        </Button>
      ) : null}
    </div>
  );
}

/** Envoltorio de query: decide entre loading / error / vacío / contenido. */
export function EstadoQuery<T>({
  cargando,
  error,
  datos,
  vacio,
  reintentar,
  children,
}: {
  cargando: boolean;
  error: unknown;
  datos: T | undefined;
  vacio?: (d: T) => boolean;
  reintentar?: () => void;
  children: (d: T) => ReactNode;
}) {
  if (cargando) return <Cargando />;
  if (error) return <ErrorEstado error={error} {...(reintentar ? { reintentar } : {})} />;
  if (datos === undefined) return <Cargando />;
  if (vacio?.(datos)) return <Vacio />;
  return <>{children(datos)}</>;
}
